import React, { useState } from 'react';
import styled from 'styled-components';
import { X, AlertCircle, User, Phone, MapPin, Calendar, FileText, Eye, Save } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 1rem;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #fecaca;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AlertIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #991b1b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #991b1b;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(153, 27, 27, 0.1);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
`;

const OrderCard = styled.div`
  background: #fef2f2;
  border: 2px solid #fecaca;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const OrderTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #991b1b;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const InfoIcon = styled.div`
  color: #dc2626;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-size: 0.9375rem;
  font-weight: 500;
  color: #1e293b;
`;

const ReasonBox = styled.div`
  background: white;
  border-left: 4px solid #dc2626;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
`;

const ReasonLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #991b1b;
  margin-bottom: 0.5rem;
`;

const ReasonText = styled.div`
  font-size: 0.9375rem;
  color: #1e293b;
  line-height: 1.5;
`;

const PrescriptionDetails = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const PrescriptionTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PrescriptionInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #64748b;
`;

const PrescriptionInfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  
  span:first-child {
    font-weight: 500;
    color: #475569;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

const ReasonInput = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #fecaca;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-family: inherit;
  min-height: 100px;
  resize: vertical;
  margin-top: 0.5rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #dc2626;
  }
`;

const ReasonInputLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #991b1b;
  display: block;
  margin-top: 1rem;
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #047857 0%, #065f46 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IncompleteOrderModal = ({ incompleteOrders, onClose }) => {
  const { t } = useTranslation();
  const [reasons, setReasons] = useState({});
  const [saving, setSaving] = useState(false);

  if (!incompleteOrders || incompleteOrders.length === 0) {
    return null;
  }

  const handleReasonChange = (index, value) => {
    setReasons(prev => ({
      ...prev,
      [index]: value
    }));
  };

  const handleSaveReasons = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Save reasons for each incomplete order
      const savePromises = incompleteOrders.map(async (item, index) => {
        const reason = reasons[index] || '';
        if (!reason.trim()) {
          return { success: true, skipped: true }; // Skip if no reason provided
        }

        const reasonData = {
          prescriptionId: item.prescription?._id || null,
          visitorId: item.visitor?._id || null,
          orderId: item.order?._id || null,
          reason: reason.trim(),
          missingStep: item.missingStep,
          userId: user.id
        };

        try {
          const response = await axios.post('/api/orders/incomplete/reason', reasonData, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          return { success: true, response: response.data };
        } catch (error) {
          console.error(`Error saving reason for order ${index + 1}:`, error);
          return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'Unknown error',
            index: index + 1
          };
        }
      });

      const results = await Promise.all(savePromises.filter(p => p !== undefined));
      
      // Check if any saves failed
      const failures = results.filter(r => !r.success && !r.skipped);
      if (failures.length > 0) {
        const errorMessages = failures.map(f => 
          `Order ${f.index}: ${f.error}`
        ).join('\n');
        alert(`Error saving some reasons:\n\n${errorMessages}\n\nPlease try again.`);
        return;
      }
      
      // All saves succeeded
      // Close modal and continue
      onClose();
    } catch (error) {
      console.error('Error saving reasons:', error);
      alert(`Error saving reasons: ${error.response?.data?.message || error.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  const allReasonsProvided = incompleteOrders.every((_, index) => {
    return reasons[index] && reasons[index].trim().length > 0;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPrescriptionTypeLabel = (type) => {
    if (!type) return 'N/A';
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <ModalOverlay>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderLeft>
            <AlertIcon>
              <AlertCircle size={24} />
            </AlertIcon>
            <HeaderTitle>
              {t('incompleteModal.unsuccessfulOrders')}{incompleteOrders.length})
            </HeaderTitle>
          </HeaderLeft>
          {allReasonsProvided && !saving && (
            <CloseButton onClick={onClose}>
              <X size={24} />
            </CloseButton>
          )}
        </ModalHeader>

        <ModalBody>
          {incompleteOrders.map((item, index) => (
            <OrderCard key={index}>
              <OrderTitle>
                <FileText size={20} />
                {t('incompleteModal.order')}{index + 1} - {item.prescription ? item.prescription.patientName : t('incompleteModal.visitorEntryOnly')}
              </OrderTitle>

              {item.prescription ? (
                <>
                  <InfoGrid>
                    <InfoItem>
                      <InfoIcon>
                        <User size={18} />
                      </InfoIcon>
                      <InfoContent>
                        <InfoLabel>{t('incompleteModal.patientName')}</InfoLabel>
                        <InfoValue>{item.prescription.patientName}</InfoValue>
                      </InfoContent>
                    </InfoItem>

                    <InfoItem>
                      <InfoIcon>
                        <Calendar size={18} />
                      </InfoIcon>
                      <InfoContent>
                        <InfoLabel>{t('incompleteModal.age')}</InfoLabel>
                        <InfoValue>{item.prescription.patientAge} {t('incompleteModal.yearsOld')}</InfoValue>
                      </InfoContent>
                    </InfoItem>

                    <InfoItem>
                      <InfoIcon>
                        <Phone size={18} />
                      </InfoIcon>
                      <InfoContent>
                        <InfoLabel>{t('incompleteModal.phoneNumber')}</InfoLabel>
                        <InfoValue>{item.prescription.phoneNumber || 'N/A'}</InfoValue>
                      </InfoContent>
                    </InfoItem>

                    <InfoItem>
                      <InfoIcon>
                        <MapPin size={18} />
                      </InfoIcon>
                      <InfoContent>
                        <InfoLabel>{t('incompleteModal.address')}</InfoLabel>
                        <InfoValue>{item.prescription.address || 'N/A'}</InfoValue>
                      </InfoContent>
                    </InfoItem>

                    <InfoItem>
                      <InfoIcon>
                        <Eye size={18} />
                      </InfoIcon>
                      <InfoContent>
                        <InfoLabel>{t('incompleteModal.prescriptionType')}</InfoLabel>
                        <InfoValue>{getPrescriptionTypeLabel(item.prescription.prescriptionType)}</InfoValue>
                      </InfoContent>
                    </InfoItem>

                    <InfoItem>
                      <InfoIcon>
                        <FileText size={18} />
                      </InfoIcon>
                      <InfoContent>
                        <InfoLabel>{t('incompleteModal.prescribedBy')}</InfoLabel>
                        <InfoValue>{item.prescription.prescribedBy || 'N/A'}</InfoValue>
                      </InfoContent>
                    </InfoItem>
                  </InfoGrid>

                  <PrescriptionDetails>
                    <PrescriptionTitle>
                      <Eye size={16} />
                      {t('incompleteModal.prescriptionDetails')}
                    </PrescriptionTitle>
                    <PrescriptionInfo>
                      <PrescriptionInfoItem>
                        <span>{t('incompleteModal.orderType')}</span>
                        <span>{item.prescription.orderType === 'professional' ? t('incompleteModal.professionalRecommendation') : (item.prescription.orderType === 'buyer' || item.prescription.orderType === 'other') ? t('incompleteModal.other') : t('incompleteModal.nA')}</span>
                      </PrescriptionInfoItem>
                      {item.prescription.selectedRecommendation && (
                        <PrescriptionInfoItem>
                          <span>{t('incompleteModal.selectedLens')}</span>
                          <span>{item.prescription.selectedRecommendation.name} ({item.prescription.selectedRecommendation.price?.toLocaleString()} {t('incompleteModal.dzd')}</span>
                        </PrescriptionInfoItem>
                      )}
                      {item.prescription.buyerChoice && (
                        <PrescriptionInfoItem>
                          <span>{t('incompleteModal.buyerChoice')}</span>
                          <span>{item.prescription.buyerChoice.frameType || 'N/A'}</span>
                        </PrescriptionInfoItem>
                      )}
                      <PrescriptionInfoItem>
                        <span>{t('incompleteModal.prescriptionDate')}</span>
                        <span>{formatDate(item.prescription.prescriptionDate)}</span>
                      </PrescriptionInfoItem>
                      <PrescriptionInfoItem>
                        <span>{t('incompleteModal.created')}</span>
                        <span>{formatDate(item.prescription.createdAt)}</span>
                      </PrescriptionInfoItem>
                    </PrescriptionInfo>
                  </PrescriptionDetails>
                </>
              ) : (
                <InfoGrid>
                  <InfoItem>
                    <InfoIcon>
                      <User size={18} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>{t('incompleteModal.visitorGender')}</InfoLabel>
                      <InfoValue>{item.visitor.gender || 'N/A'}</InfoValue>
                    </InfoContent>
                  </InfoItem>

                  <InfoItem>
                    <InfoIcon>
                      <Calendar size={18} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>{t('incompleteModal.ageGroup')}</InfoLabel>
                      <InfoValue>{item.visitor.ageGroup || 'N/A'}</InfoValue>
                    </InfoContent>
                  </InfoItem>

                  <InfoItem>
                    <InfoIcon>
                      <Calendar size={18} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>{t('incompleteModal.visitDate')}</InfoLabel>
                      <InfoValue>{formatDate(item.visitor.visitDate)}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </InfoGrid>
              )}

              <ReasonBox>
                <ReasonLabel>{t('incompleteModal.reasonForUnsuccessfulOrder')}</ReasonLabel>
                <ReasonText>{item.reason}</ReasonText>
                {item.order && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#64748b' }}>
                    {t('incompleteModal.orderNumber')} {item.order.orderNumber} {t('incompleteModal.status')} {item.order.status}
                  </div>
                )}
              </ReasonBox>

              <ReasonInputLabel htmlFor={`reason-${index}`}>
                {t('incompleteModal.pleaseEnterReasonWhyOrder')}
              </ReasonInputLabel>
              <ReasonInput
                id={`reason-${index}`}
                value={reasons[index] || ''}
                onChange={(e) => handleReasonChange(index, e.target.value)}
                placeholder={t('incompleteModal.enterReasonForIncompleteOrder')}
                required
              />
            </OrderCard>
          ))}
        </ModalBody>

        <ModalFooter>
          {!allReasonsProvided && (
            <div style={{ 
              flex: 1, 
              color: '#dc2626', 
              fontSize: '0.875rem', 
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <AlertCircle size={18} />
              {t('incompleteModal.pleaseProvideAReasonFor')}
            </div>
          )}
          <SaveButton 
            onClick={handleSaveReasons} 
            disabled={!allReasonsProvided || saving}
          >
            <Save size={18} />
            {saving ? t('incompleteModal.saving') : t('incompleteModal.saveReasonsContinue')}
          </SaveButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default IncompleteOrderModal;

